import { Box, useTheme } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency, formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { Chart as ChartJS } from "chart.js/auto";
import AnnotationPlugin from "chartjs-plugin-annotation";
import type { FC } from "react";
import { Bar } from "react-chartjs-2";

ChartJS.register(AnnotationPlugin);

type Props = {
  readonly incomesAmount: number;
  readonly insuranceExpenses: number;
  readonly financialProductExpenses: number;
  readonly loanExpenses: number;
  readonly otherExpenses: number;
};

export const RecapChart: FC<Props> = ({ incomesAmount, insuranceExpenses, financialProductExpenses, loanExpenses, otherExpenses }) => {
  const theme = useTheme();
  const { t } = useResource();

  const expensesAmount = insuranceExpenses + financialProductExpenses + loanExpenses + otherExpenses;
  const otherExpensesAmount = insuranceExpenses + loanExpenses + otherExpenses;

  const isLabelVisibleByValues = (value: number): boolean => {
    const maxValue = Math.max(incomesAmount, financialProductExpenses, otherExpensesAmount);
    return value >= maxValue * 0.12;
  };

  const options = {
    barPercentage: 0.95,
    categoryPercentage: 0.95,
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: {
      topLeft: 6,
      topRight: 6,
      bottomLeft: 6,
      bottomRight: 6,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || "";

            if (label) {
              const datasetName = context.dataset.label;

              switch (datasetName) {
                case t(Resources.AnalysisDetail.Budget.Incomes.Title): {
                  return `${t(Resources.AnalysisDetail.Budget.Incomes.Title)}: ${formatCurrency(context.dataset.data[context.dataIndex], CurrencyCode.CZK, 0)}`;
                }
                case t(Resources.AnalysisDetail.Budget.Recap.ExpensesFinancialProduct): {
                  return `${t(Resources.AnalysisDetail.Budget.Recap.ExpensesFinancialProduct)}: ${formatCurrency(context.dataset.data[context.dataIndex], CurrencyCode.CZK, 0)}`;
                }
                case t(Resources.AnalysisDetail.Budget.Expenses.Title): {
                  return `${t(Resources.AnalysisDetail.Budget.Expenses.Title)}: ${formatCurrency(context.dataset.data[context.dataIndex], CurrencyCode.CZK, 0)}`;
                }
                default:
                  throw new Error();
              }
            }
          },
        },
      },
      annotation: {
        annotations: {
          incomeLabel: {
            display: isLabelVisibleByValues(incomesAmount),
            type: "label" as const,
            xValue: 0,
            yValue: incomesAmount / 2,
            backgroundColor: "transparent",
            content: formatCurrencyShortText(incomesAmount),
            color: colors.white,
            font: {
              weight: "bold" as const,
              size: 12,
            },
          },
          financialProductLabel: {
            display: isLabelVisibleByValues(financialProductExpenses),
            type: "label" as const,
            xValue: 1,
            yValue: expensesAmount - financialProductExpenses / 2,
            backgroundColor: "transparent",
            content: formatCurrencyShortText(financialProductExpenses),
            color: colors.white,
            font: {
              weight: "bold" as const,
              size: 12,
            },
          },
          otherExpensesLabel: {
            display: isLabelVisibleByValues(otherExpensesAmount),
            type: "label" as const,
            xValue: 1,
            yValue: otherExpensesAmount / 2,
            backgroundColor: "transparent",
            content: formatCurrencyShortText(otherExpensesAmount),
            color: colors.white,
            font: {
              weight: "bold" as const,
              size: 12,
            },
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          color: colors.textSecondary,
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          display: false,
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        grace: "1",
      },
    },
  };

  return (
    <Box position="relative" width={"100%"} minHeight={210}>
      <Bar
        options={options}
        data={{
          labels: [t(Resources.AnalysisDetail.Budget.Incomes.Title), t(Resources.AnalysisDetail.Budget.Expenses.Title)],
          datasets: [
            {
              label: t(Resources.AnalysisDetail.Budget.Incomes.Title),
              data: [incomesAmount, 0],
              backgroundColor: colors.chartPositive,
            },
            {
              label: t(Resources.AnalysisDetail.Budget.Expenses.Title),
              data: [0, otherExpensesAmount],
              backgroundColor: theme.palette.error.main,
              borderRadius: 0,
              order: 1,
            },
            {
              label: t(Resources.AnalysisDetail.Budget.Recap.ExpensesFinancialProduct),
              data: [0, financialProductExpenses],
              backgroundColor: colors.brandPrimary,
              order: 2,
            },
          ],
        }}
      />
    </Box>
  );
};
